import { Link } from 'react-router-dom';
import type { UseFormReturn } from 'react-hook-form';
import { ArrowLeft, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { PageHeader } from '@/components/feedback/PageHeader';
import { ScrollPage } from '@/components/layout/PageContainer';
import type { LocationFormInput } from '../schema';

interface LocationFormProps {
  mode: 'create' | 'update';
  form: UseFormReturn<LocationFormInput>;
  onSubmit: (input: LocationFormInput) => void;
  isPending: boolean;
  pageTitle: string;
  pageDescription: string;
}

export function LocationForm({
  mode,
  form,
  onSubmit,
  isPending,
  pageTitle,
  pageDescription,
}: LocationFormProps) {
  return (
    <ScrollPage>
      <PageHeader
        title={pageTitle}
        description={pageDescription}
        actions={
          <Button asChild variant="outline">
            <Link to="/location-list">
              <ArrowLeft className="h-4 w-4" />
              Back to locations
            </Link>
          </Button>
        }
      />
      <Form {...form}>
        <form className="space-y-4" onSubmit={form.handleSubmit(onSubmit)} noValidate>
          <Card>
            <CardHeader>
              <CardTitle>Location details</CardTitle>
              <CardDescription>
                Pick a unique, descriptive name. Locations scope campaigns and dialer assignments.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <FormField
                control={form.control}
                name="locationName"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Location name</FormLabel>
                    <FormControl>
                      <Input
                        {...field}
                        placeholder="e.g. Mumbai-DC1"
                        autoFocus={mode === 'create'}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </CardContent>
          </Card>

          <div className="flex items-center justify-end gap-2">
            <Button asChild variant="ghost">
              <Link to="/location-list">Cancel</Link>
            </Button>
            <Button type="submit" disabled={isPending}>
              {isPending && <Loader2 className="h-4 w-4 animate-spin" />}
              {mode === 'create' ? 'Create location' : 'Save changes'}
            </Button>
          </div>
        </form>
      </Form>
    </ScrollPage>
  );
}
